import { produce, Draft } from 'immer'
import { IAction, TActionTypes } from '@Root/types'
import map from 'lodash/map'
import without from 'lodash/without'

import {
  ISpeakerVoiceState,
  TSpeakerVoiceActionTypes,
  SpeakerVoiceActionTypes,
  IFetchSpeakerVoicesSuccessResponseBody,
  IFetchLanguagesSuccessResponseBody,
  IUpdateSpeakerVoiceSuccessResponseBody,
  IRemoveSpeakerVoicesFromWorkspaceSuccessResponseBody,
  ICreateSpeakerVoiceSuccessResponseBody,
  IGenerateDesignSampleSuccessResponseBody,
  IGenerateDesignSampleFailurePayload,
  IChangeSelectedSpeakerVoicesPayload,
  ISetSearchTermPayload,
  ISetOpenedSpeakerVoiceIdPayload,
  ISetIsNewSpeakerVoicePayload,
  IChangeSelectedLanguageNamePayload,
  ISetSelectedLanguagesNamesPayload,
} from './types'

import {
  WorkspaceActionTypes
} from '@SiteModules/Workspace/types'

export const initialState: ISpeakerVoiceState = {
  speakerVoices: [],
  languages: {},
  selectedIds: [],
  usedLanguages: {},
  selectedLanguagesNames: [],
  hasLoadedInitially: false,
  loading: false,
  isGeneratingDesignSample: false,
  generatedDesignSampleError: null,
  isNewSpeakerVoice: false,
  page: {
    currentPage: 1,
    totalCount: 0,
    totalPages: 1,
    shouldCheckAutoFill: false,
  }
}

type TAction = TActionTypes | TSpeakerVoiceActionTypes

export const speakerVoicesReducer = (
  state: ISpeakerVoiceState = initialState,
  action: TAction
): ISpeakerVoiceState => {
  return produce(state, draft => {
    switch (action.type) {
      case WorkspaceActionTypes.SET_CURRENT_WORKSPACE:
      case WorkspaceActionTypes.RESET_CURRENT_WORKSPACE:
        reset(draft)
        break

      case SpeakerVoiceActionTypes.FETCH_SPEAKER_VOICES_SUCCESS:
        fetchSuccess(draft, action)
        break

      case SpeakerVoiceActionTypes.FETCH_LANGUAGES_SUCCESS:
        fetchLanguagesSuccess(draft, action)
        break

      case SpeakerVoiceActionTypes.RESET_SHOULD_CHECK_AUTO_FILL:
        resetShouldCheckAutoFill(draft)
        break

      case SpeakerVoiceActionTypes.SET_SHOULD_CHECK_AUTO_FILL:
        setShouldCheckAutoFill(draft)
        break

      case SpeakerVoiceActionTypes.UPDATE_SPEAKER_VOICE_SUCCESS:
        updateSuccess(draft, action)
        break

      case SpeakerVoiceActionTypes.REMOVE_SPEAKER_VOICES_FROM_WORKSPACE_SUCCESS:
        removeFromWorkspaceSuccess(draft, action)
        break

      case SpeakerVoiceActionTypes.CREATE_SPEAKER_VOICE_SUCCESS:
        createSpeakerVoiceSuccess(draft, action)
        break

      case SpeakerVoiceActionTypes.GENERATE_DESIGN_SAMPLE:
        startGeneratingDesignSample(draft)
        break

      case SpeakerVoiceActionTypes.GENERATE_DESIGN_SAMPLE_SUCCESS:
        generateDesignSampleSuccess(draft, action)
        break

      case SpeakerVoiceActionTypes.GENERATE_DESIGN_SAMPLE_FAILURE:
        generateDesignSampleFailure(draft, action)
        break

      case SpeakerVoiceActionTypes.RESET_GENERATED_DESIGN_SAMPLE:
        resetGeneratedDesignSample(draft)
        break

      case SpeakerVoiceActionTypes.CHANGE_SELECTED_SPEAKER_VOICES:
        changeSelected(draft, action)
        break

      case SpeakerVoiceActionTypes.RESET_SELECTED_SPEAKER_VOICES:
        resetSelected(draft)
        break

      case SpeakerVoiceActionTypes.SET_SEARCH_TERM:
        setSearchTerm(draft, action)
        break

      case SpeakerVoiceActionTypes.SET_OPENED_SPEAKER_VOICE_ID:
        setOpenedSpeakerVoiceId(draft, action)
        break

      case SpeakerVoiceActionTypes.RESET_OPENED_SPEAKER_VOICE_ID:
        resetOpenedSpeakerVoiceId(draft)
        break

      case SpeakerVoiceActionTypes.SET_IS_NEW_SPEAKER_VOICE:
        setIsNewSpeakerVoice(draft, action)
        break

      case SpeakerVoiceActionTypes.RESET_IS_NEW_SPEAKER_VOICE:
        resetIsNewSpeakerVoice(draft)
        break

      case SpeakerVoiceActionTypes.CHANGE_SELECTED_LANGUAGE:
        changeSelectedLanguageName(draft, action)
        break

      case SpeakerVoiceActionTypes.SET_SELECTED_LANGUAGES:
        setSelectedLanguagesNames(draft, action)
        break

      case SpeakerVoiceActionTypes.FETCH_SPEAKER_VOICES:
      case SpeakerVoiceActionTypes.FETCH_LANGUAGES:
      case SpeakerVoiceActionTypes.UPDATE_SPEAKER_VOICE:
      case SpeakerVoiceActionTypes.REMOVE_SPEAKER_VOICES_FROM_WORKSPACE:
      case SpeakerVoiceActionTypes.CREATE_SPEAKER_VOICE:
        startLoading(draft)
        break

      case SpeakerVoiceActionTypes.FETCH_SPEAKER_VOICES_FAILURE:
      case SpeakerVoiceActionTypes.FETCH_LANGUAGES_FAILURE:
      case SpeakerVoiceActionTypes.UPDATE_SPEAKER_VOICE_FAILURE:
      case SpeakerVoiceActionTypes.REMOVE_SPEAKER_VOICES_FROM_WORKSPACE_FAILURE:
      case SpeakerVoiceActionTypes.CREATE_SPEAKER_VOICE_FAILURE:
        stopLoading(draft)
        break

      default:
        break
    }
  })
}

const reset = (draft: Draft<ISpeakerVoiceState>) => {
  draft.hasLoadedInitially = false
  draft.speakerVoices = []
  draft.selectedIds = []
  draft.usedLanguages = {}
  draft.selectedLanguagesNames = []
  draft.isGeneratingDesignSample = false
  draft.generatedDesignSampleUrl = undefined
  draft.generatedDesignSampleError = null
  draft.page = {
    currentPage: 1,
    totalCount: 0,
    totalPages: 1,
    shouldCheckAutoFill: false,
  }
}

function isIFetchSuccessAction(arg: any): arg is IAction<IFetchSpeakerVoicesSuccessResponseBody> {
  return arg.type === SpeakerVoiceActionTypes.FETCH_SPEAKER_VOICES_SUCCESS
}

const fetchSuccess = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isIFetchSuccessAction(action)) { return }

  if (action.payload.currentPage === 1 || action.payload.currentPage === draft.page.currentPage + 1) {
    const newSpeakerVoices = map(action.payload.data, data => {
      return ({
        id: data.id,
        attributes: data.attributes
      })
    })

    if (action.payload.currentPage === 1) {
      draft.speakerVoices = newSpeakerVoices
      draft.page.shouldCheckAutoFill = true
    } else {
      draft.speakerVoices = draft.speakerVoices.concat(newSpeakerVoices)
    }

    draft.page.totalCount = action.payload.totalCount
    draft.page.totalPages = action.payload.totalPages
    draft.page.currentPage = action.payload.currentPage
  }

  if (action.payload.usedLanguages !== undefined) {
    draft.usedLanguages = action.payload.usedLanguages
  }

  draft.hasLoadedInitially = true
  draft.loading = false
}

function isIFetchLanguagesSuccessAction(arg: any): arg is IAction<IFetchLanguagesSuccessResponseBody> {
  return arg.type === SpeakerVoiceActionTypes.FETCH_LANGUAGES_SUCCESS
}

const fetchLanguagesSuccess = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isIFetchLanguagesSuccessAction(action)) { return }

  draft.languages = action.payload
  draft.loading = false
}

const resetShouldCheckAutoFill = (draft: Draft<ISpeakerVoiceState>) => {
  draft.page.shouldCheckAutoFill = false
}

const setShouldCheckAutoFill = (draft: Draft<ISpeakerVoiceState>) => {
  draft.page.shouldCheckAutoFill = true
}

const startLoading = (draft: Draft<ISpeakerVoiceState>) => {
  draft.loading = true
}

const stopLoading = (draft: Draft<ISpeakerVoiceState>) => {
  draft.loading = false
}

function isIUpdateSuccessAction(arg: any): arg is IAction<IUpdateSpeakerVoiceSuccessResponseBody> {
  return arg.type === SpeakerVoiceActionTypes.UPDATE_SPEAKER_VOICE_SUCCESS
}

const updateSuccess = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isIUpdateSuccessAction(action)) { return }

  const index = draft.speakerVoices.findIndex(sv => sv.id === action.payload.data.id)
  if (index === -1) { return }

  draft.speakerVoices.splice(index, 1)
  draft.speakerVoices.unshift({
    id: action.payload.data.id,
    attributes: action.payload.data.attributes,
  })
  draft.loading = false
}

function isIRemoveFromWorkspaceSuccessAction(arg: any): arg is IAction<IRemoveSpeakerVoicesFromWorkspaceSuccessResponseBody> {
  return arg.type === SpeakerVoiceActionTypes.REMOVE_SPEAKER_VOICES_FROM_WORKSPACE_SUCCESS
}

const removeFromWorkspaceSuccess = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isIRemoveFromWorkspaceSuccessAction(action)) { return }

  draft.speakerVoices = draft.speakerVoices.filter(sv => !action.payload.speakerVoiceIds.includes(sv.id))
  draft.selectedIds = draft.selectedIds.filter(id => !action.payload.speakerVoiceIds.includes(id))
  draft.loading = false
}

function isICreateSpeakerVoiceSuccessAction(arg: any): arg is IAction<ICreateSpeakerVoiceSuccessResponseBody> {
  return arg.type === SpeakerVoiceActionTypes.CREATE_SPEAKER_VOICE_SUCCESS
}

const createSpeakerVoiceSuccess = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isICreateSpeakerVoiceSuccessAction(action)) { return }

  const { id, attributes } = action.payload.data

  draft.speakerVoices.unshift({
    id,
    attributes,
  })
  if (action.payload.openAfterCreation) {
    draft.openedSpeakerVoiceId = id
    draft.isNewSpeakerVoice = true
  }
  draft.loading = false
}

function isIGenerateDesignSampleSuccessAction(arg: any): arg is IAction<IGenerateDesignSampleSuccessResponseBody> {
  return arg.type === SpeakerVoiceActionTypes.GENERATE_DESIGN_SAMPLE_SUCCESS
}

const startGeneratingDesignSample = (draft: Draft<ISpeakerVoiceState>) => {
  draft.isGeneratingDesignSample = true
  draft.generatedDesignSampleUrl = undefined
  draft.generatedDesignSampleError = null
}

const generateDesignSampleSuccess = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isIGenerateDesignSampleSuccessAction(action)) { return }

  draft.isGeneratingDesignSample = false
  draft.generatedDesignSampleUrl = action.payload.audioUrl
  draft.generatedDesignSampleError = null
}

function isIGenerateDesignSampleFailureAction(arg: any): arg is IAction<IGenerateDesignSampleFailurePayload> {
  return arg.type === SpeakerVoiceActionTypes.GENERATE_DESIGN_SAMPLE_FAILURE
}

const generateDesignSampleFailure = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isIGenerateDesignSampleFailureAction(action)) { return }

  draft.isGeneratingDesignSample = false
  draft.generatedDesignSampleUrl = undefined
  draft.generatedDesignSampleError = action.payload.message
}

const resetGeneratedDesignSample = (draft: Draft<ISpeakerVoiceState>) => {
  draft.isGeneratingDesignSample = false
  draft.generatedDesignSampleUrl = undefined
  draft.generatedDesignSampleError = null
}

function isIChangeSelectedAction(arg: any): arg is IAction<IChangeSelectedSpeakerVoicesPayload> {
  return arg.type === SpeakerVoiceActionTypes.CHANGE_SELECTED_SPEAKER_VOICES
}

const changeSelected = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isIChangeSelectedAction(action)) { return }

  const { selectSpeakerVoice, speakerVoiceId } = action.payload

  if (selectSpeakerVoice) {
    draft.selectedIds.push(speakerVoiceId)
  } else {
    draft.selectedIds = without(draft.selectedIds, speakerVoiceId)
  }
}

const resetSelected = (draft: Draft<ISpeakerVoiceState>) => {
  draft.selectedIds = []
}

function isISetSearchTermAction(arg: any): arg is IAction<ISetSearchTermPayload> {
  return arg.type === SpeakerVoiceActionTypes.SET_SEARCH_TERM
}

const setSearchTerm = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isISetSearchTermAction(action)) { return }

  draft.page.searchTerm = action.payload.searchTerm
}

function isISetOpenedSpeakerVoiceIdAction(arg: any): arg is IAction<ISetOpenedSpeakerVoiceIdPayload> {
  return arg.type === SpeakerVoiceActionTypes.SET_OPENED_SPEAKER_VOICE_ID
}

const setOpenedSpeakerVoiceId = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isISetOpenedSpeakerVoiceIdAction(action)) { return }

  draft.openedSpeakerVoiceId = action.payload.speakerVoiceId
}

const resetOpenedSpeakerVoiceId = (draft: Draft<ISpeakerVoiceState>) => {
  draft.openedSpeakerVoiceId = undefined
  draft.isNewSpeakerVoice = false
}

function isISetIsNewSpeakerVoiceAction(arg: any): arg is IAction<ISetIsNewSpeakerVoicePayload> {
  return arg.type === SpeakerVoiceActionTypes.SET_IS_NEW_SPEAKER_VOICE
}

const setIsNewSpeakerVoice = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isISetIsNewSpeakerVoiceAction(action)) { return }

  draft.isNewSpeakerVoice = action.payload.isNew
}

const resetIsNewSpeakerVoice = (draft: Draft<ISpeakerVoiceState>) => {
  draft.isNewSpeakerVoice = false
}

function isIChangeSelectedLanguageNameAction(arg: any): arg is IAction<IChangeSelectedLanguageNamePayload> {
  return arg.type === SpeakerVoiceActionTypes.CHANGE_SELECTED_LANGUAGE
}

const changeSelectedLanguageName = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isIChangeSelectedLanguageNameAction(action)) { return }

  const { languageName, select } = action.payload

  if (select) {
    draft.selectedLanguagesNames.push(languageName)
  } else {
    draft.selectedLanguagesNames = without(draft.selectedLanguagesNames, languageName)
  }
}

function isISetSelectedLanguagesNamesAction(arg: any): arg is IAction<ISetSelectedLanguagesNamesPayload> {
  return arg.type === SpeakerVoiceActionTypes.SET_SELECTED_LANGUAGES
}

const setSelectedLanguagesNames = (draft: Draft<ISpeakerVoiceState>, action: TAction) => {
  if (!isISetSelectedLanguagesNamesAction(action)) { return }

  draft.selectedLanguagesNames = action.payload.selectedLanguagesNames
}
